import React, { createContext, ReactNode, useContext, useMemo, useState } from 'react';
import { useQuery } from 'react-query';

import { getSnapshots } from 'apis/SnapshotsAPI';
import { useRootContext } from 'components/layout/RootContext';
import { usePublicationContext } from 'components/Publication/PublicationContext';
import { useAppSelector } from 'store/hook';
import { SnapshotDataType } from 'types/snapshot';

type SnapshotContentType = {
  isLoading?: boolean;
  snapshot: SnapshotDataType | undefined;
  currentSnapshot?: SnapshotDataType | undefined;
  historicalSnapshots?: SnapshotDataType[];
  draftSnapshots?: SnapshotDataType[];
  refetch: () => void;
  setSnapshot: (value: SnapshotDataType | undefined) => void;
};

export const SnapshotContext = createContext<SnapshotContentType>({
  isLoading: false,
  snapshot: undefined,
  currentSnapshot: undefined,
  draftSnapshots: [],
  historicalSnapshots: [],
  refetch: () => {
    // Does Nothing
  },
  setSnapshot: () => {
    // Does Nothing
  },
});

export const useSnapshotContext = () => useContext(SnapshotContext);

const SnapshotProvider = ({ communityId, children }: { communityId: number; children: ReactNode }) => {
  const { showMessage } = useRootContext();
  const { publication } = usePublicationContext();
  const { contextCompany } = useAppSelector((state) => state.global);

  const [currentSnapshot, setCurrentSnapshot] = useState<SnapshotDataType | undefined>();
  const [historicalSnapshots, setHistoricalSnapshots] = useState<SnapshotDataType[]>([]);
  const [draftSnapshots, setDraftSnapshots] = useState<SnapshotDataType[]>([]);
  const [snapshot, setSnapshot] = useState<SnapshotDataType | undefined>(undefined);

  const snapshotQueryParams = {
    community_ids: [communityId],
    ...(publication?.hash ? { public_hash: publication.hash } : {}),
  };

  const { isLoading, refetch } = useQuery({
    queryKey: ['snapshot', 'list', communityId, contextCompany, publication?.hash],
    queryFn: () => getSnapshots(snapshotQueryParams),
    onSuccess: (data: SnapshotDataType[]) => {
      const snapshotList = Array.isArray(data) ? data : [];
      const draftSnapshots = snapshotList.filter((item: SnapshotDataType) => item.draft);
      const snapshots = snapshotList.filter((item: SnapshotDataType) => !item.draft);
      // Select the latest snapshot from both active and draft snapshots if available
      const latestSnapshot = snapshots.length ? snapshots.shift() : undefined;
      const latestDraftSnapshot = draftSnapshots.length ? draftSnapshots[0] : undefined;

      // Prevent draft status from ever being considered current
      if (!latestSnapshot && latestDraftSnapshot) {
        setSnapshot(latestDraftSnapshot);
      } else {
        setSnapshot(latestSnapshot);
      }

      setCurrentSnapshot(latestSnapshot);
      setHistoricalSnapshots(snapshots ?? []);
      setDraftSnapshots(draftSnapshots ?? []);
    },
    onError: () => {
      showMessage('error', 'Failed to fetch Community Data Snapshots');
    },
    enabled: Boolean(communityId),
    refetchOnWindowFocus: false,
  });

  return (
    <SnapshotContext.Provider
      value={{ refetch, isLoading, snapshot, currentSnapshot, historicalSnapshots, draftSnapshots, setSnapshot }}
    >
      {children}
    </SnapshotContext.Provider>
  );
};

export default SnapshotProvider;
